import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Product, Variant } from 'shared/types/product';
import { discardRepeatedValues } from '../helpers/discardRepeatedValues';
import { useFormat } from 'helpers/hooks/useFormat';
import { SelectedAttributes } from '../types';
import sizeData from 'public/locales/en/product.json';
import { CheckIcon } from '@heroicons/react/24/solid';
import { usePathname, useSearchParams } from 'next/navigation';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import useLocale from 'helpers/hooks/useLocale';
import { useCookies } from 'react-cookie';
import { TableSet } from 'shared/types/product/Product';
import { formatSize } from 'helpers/constants/sizes';

const CenterImageCrop = ({
  imgUrl,
  altText = '',
  variant,
  isActive,
}: {
  imgUrl: string;
  altText?: string;
  variant: Variant;
  isActive: boolean;
}) => {
  return (
    <div className="relative h-full w-full cursor-pointer rounded-md">
      <div
        className="h-[95%] w-[95%] bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url(${imgUrl})`,
          backgroundPosition: 'center',
          backgroundSize: `${variant?.attributes?.cropX}px ${variant?.attributes?.cropY}px`,
        }}
      ></div>
      {isActive && <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-[#1DCE8F]" />}
    </div>
  );
};

const sortVariantsByShoeSize = (a: Variant, b: Variant) => {
  const sizeA = parseFloat(a.attributes?.shoesize || '0');
  const sizeB = parseFloat(b.attributes?.shoesize || '0');
  return sizeA - sizeB;
};

const attributeMapping: Record<string, string> = {
  waistsize: 'waist',
  leglength: 'leg',
};

const sizeLabels = sizeData?.sizeLabels;

const sizeKeys = Object.keys(sizeLabels);
const sizeMap = sizeKeys?.map((key, index) => ({
  index,
  key,
  name: sizeLabels[key as keyof typeof sizeLabels],
}));

type LegLengthVariant = {
  attributes: {
    leg: string;
    size?: string;
  };
};

const VariantOptions = ({
  type,
  variants,
  showSizeGuide,
  product,
  optionsOrder,
  changeRoute,
  onChangeVariant,
  selectedAttributes,
  setSelectedAttributes,
  isGPSCategory,
  setHoveredImage,
  setVariantSelected,
  variantSelected,
  tableSet,
  setIsCartDisable,
  handleColorVariant,
}: {
  type: string;
  variants: Variant[];
  showSizeGuide?: React.MouseEventHandler<HTMLSpanElement>;
  product: Product;
  optionsOrder: string[];
  changeRoute: (updatedSku: string) => void;
  onChangeVariant: (updatedSku: string) => void;
  selectedAttributes: SelectedAttributes;
  setSelectedAttributes: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  isGPSCategory: boolean;
  setHoveredImage?: (img: string) => void | null | undefined;
  setVariantSelected: any;
  variantSelected: any;
  tableSet: TableSet[];
  setIsCartDisable?: any;
  handleColorVariant?: (variantSku: string) => void;
}) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const sizeAccordingToColour = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.colour === i?.attributes?.colour);
  }, [selectedAttributes?.colour]);

  const legSizeAccordingToWiast = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.waist === i?.attributes?.waist);
  }, [selectedAttributes?.size, selectedAttributes?.waist, selectedAttributes?.colour]);

  const legSizeAccordingToSizeAndColour = useMemo(() => {
    return product?.variants?.filter(
      (i: Variant) =>
        selectedAttributes?.size === i?.attributes?.size && selectedAttributes?.colour === i?.attributes?.colour,
    );
  }, [selectedAttributes?.size, selectedAttributes?.colour]);

  const legSizeAccordingToWiastAndColour = useMemo(() => {
    return product?.variants?.filter(
      (i: Variant) =>
        selectedAttributes?.waist === i?.attributes?.waist && selectedAttributes?.colour === i?.attributes?.colour,
    );
  }, [selectedAttributes?.waist, selectedAttributes?.colour]);

  const colorAccordingToWaist = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.waist === i?.attributes?.waist);
  }, [selectedAttributes?.waist]);

  const WaistAccordingTocolor = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.colour === i?.attributes?.colour);
  }, [selectedAttributes?.colour]);

  const handAccordingToSize = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.size === i?.attributes?.size);
  }, [selectedAttributes?.size]);

  const handAccordingToSizeAndColour = useMemo(() => {
    return product?.variants?.filter(
      (i: Variant) =>
        selectedAttributes?.size === i?.attributes?.size && selectedAttributes?.colour === i?.attributes?.colour,
    );
  }, [selectedAttributes?.size, selectedAttributes?.colour]);

  const colourAccordingToSize = useMemo(() => {
    return product?.variants?.filter((i: Variant) => selectedAttributes?.size === i?.attributes?.size);
  }, [selectedAttributes?.size]);

  const filteredVariants = useMemo(() => {
    let filterVariants: Variant[] = [];

    if (selectedAttributes?.colour && selectedAttributes?.hand && selectedAttributes?.size) {
      filterVariants = product?.variants?.filter(
        (variant) =>
          variant?.attributes?.hand === selectedAttributes?.hand &&
          variant?.attributes?.colour === selectedAttributes?.colour &&
          variant?.attributes?.size === selectedAttributes?.size,
      );
    } else if (selectedAttributes?.colour && selectedAttributes?.waist && selectedAttributes?.leg) {
      filterVariants = product?.variants?.filter(
        (variant) =>
          variant?.attributes?.leg === selectedAttributes?.leg &&
          variant?.attributes?.colour === selectedAttributes?.colour &&
          variant?.attributes?.waist === selectedAttributes?.waist,
      );
    } else if (selectedAttributes?.colour && selectedAttributes?.size && selectedAttributes?.leg) {
      filterVariants = product?.variants?.filter(
        (variant) =>
          variant?.attributes?.leg === selectedAttributes?.leg &&
          variant?.attributes?.colour === selectedAttributes?.colour &&
          variant?.attributes?.size === selectedAttributes?.size,
      );
    } else if (selectedAttributes?.colour && selectedAttributes?.waist) {
      filterVariants = product?.variants?.filter(
        (variant) =>
          variant?.attributes?.colour === selectedAttributes?.colour &&
          variant?.attributes?.waist === selectedAttributes?.waist,
      );
    } else if (selectedAttributes?.colour && selectedAttributes?.shoesize) {
      filterVariants = product?.variants?.filter(
        (variant) =>
          variant?.attributes?.shoesize === selectedAttributes?.shoesize &&
          variant?.attributes?.colour === selectedAttributes?.colour,
      );
    } else if (selectedAttributes?.colour && selectedAttributes?.size) {
      filterVariants = product?.variants?.filter(
        (variant) =>
          variant?.attributes?.size === selectedAttributes?.size &&
          variant?.attributes?.colour === selectedAttributes?.colour,
      );
    } else if (
      selectedAttributes?.colour &&
      !selectedAttributes?.waist &&
      !selectedAttributes?.shoesize &&
      !selectedAttributes?.size
    ) {
      filterVariants = product?.variants?.filter(
        (variant) => variant?.attributes?.colour === selectedAttributes?.colour,
      );
    }
    return filterVariants;
  }, [
    selectedAttributes.colour,
    selectedAttributes.hand,
    selectedAttributes.size,
    selectedAttributes.waist,
    selectedAttributes.leg,
    selectedAttributes.shoesize,
    product?.variants,
  ]);
  const validOptionsOrder = optionsOrder?.filter((attribute) => {
    const mappedAttribute = attributeMapping?.[attribute] || attribute;
    return product?.variants?.some((variant) => variant?.attributes?.[mappedAttribute]);
  });
  useEffect(() => {
    const areAllAttributesSelected = validOptionsOrder?.every((attribute) => {
      const mappedAttribute = attributeMapping?.[attribute] || attribute;
      return (selectedAttributes as any)?.[mappedAttribute]?.trim?.() !== '';
    });

    setIsCartDisable?.(!areAllAttributesSelected);

    if (filteredVariants?.length > 0) {
      const firstVariantSku = filteredVariants?.[0]?.sku;
      if (firstVariantSku) {
        changeRoute(firstVariantSku);
        onChangeVariant(firstVariantSku);
      }
    }

    const hasAnySelected = Object.values(selectedAttributes)?.some((val) => val?.trim?.() !== '');
    if (hasAnySelected !== variantSelected) {
      setVariantSelected(hasAnySelected);
    }
  }, [filteredVariants, variantSelected]);

  useEffect(() => {
    if (validOptionsOrder?.length === 1) {
      const attribute = validOptionsOrder[0];
      const mappedAttribute = attributeMapping?.[attribute] || attribute;
      // Get all available options for this attribute from variants
      const availableOptions = Array.from(
        new Set(product?.variants?.map((v) => v?.attributes?.[mappedAttribute]).filter(Boolean)),
      );
      // If only one option is available and not already selected, select it
      if (availableOptions.length === 1 && (selectedAttributes as any)?.[mappedAttribute] !== availableOptions[0]) {
        setSelectedAttributes((prev) => ({
          ...prev,
          [mappedAttribute]: availableOptions[0],
        }));
      }
    }
  }, [selectedAttributes]);
  switch (type) {
    case 'colour':
      const colourContent = variants || [];
      // colorAccordingToWaist?.length > 0
      //   ? discardRepeatedValues(colorAccordingToWaist, 'colour')
      // : // : colourAccordingToSize?.length > 0
      // ? discardRepeatedValues(colourAccordingToSize, 'colour')
      // variants;
      const sortedColourContent = [...colourContent]?.sort((a, b) => {
        const colourA = a?.attributes?.colour?.toLowerCase() || '';
        const colourB = b?.attributes?.colour?.toLowerCase() || '';
        return colourA?.localeCompare(colourB);
      });

      return sortedColourContent?.length > 0 ? (
        <div className="flex flex-col items-start justify-start">
          <span className="mb-13 mt-4 text-14 font-normal capitalize">
            {formatProductMessage({ id: 'choose.colour', defaultMessage: 'Choose Colour' })}
          </span>
          {isGPSCategory ? (
            <select
              className="h-30 gap-y-4 rounded-md border p-2 px-12 text-14 font-normal capitalize"
              style={{
                borderColor: 'green',
                width: '50%',
                height: '30px',
                maxHeight: '30px',
                color: 'black',
              }}
              aria-label="Select Colour"
              value={selectedAttributes?.colour || ''}
              onChange={(e) =>
                setSelectedAttributes((prevAttributes) => ({
                  ...prevAttributes,
                  colour: e.target.value,
                }))
              }
            >
              <option value="" disabled>
                {formatProductMessage({ id: 'select.colour', defaultMessage: 'Select Colour' })}
              </option>
              {sortedColourContent?.map((variant, index) => (
                <option key={index} value={variant?.attributes?.colour || ''}>
                  {variant?.attributes?.colour
                    ? variant?.attributes?.colour?.charAt(0)?.toUpperCase() + variant?.attributes?.colour?.slice(1)
                    : ''}
                </option>
              ))}
            </select>
          ) : (
            <div className="ml-5 flex flex-wrap gap-12">
              {sortedColourContent?.map((variant, index) => {
                const img = Array.isArray(variant?.images) && variant.images.length > 0 ? variant.images?.[0] : null;
                if (!img) {
                  return null;
                }

                const isActive =
                  // (sortedColourContent?.length === 1 && validOptionsOrder?.length === 1) ||
                  selectedAttributes?.colour === variant?.attributes?.colour && variant?.attributes?.colour !== '';
                // if (validOptionsOrder?.length === 1 && selectedAttributes?.colour !== variant?.attributes?.colour) {
                //   setSelectedAttributes((prevAttributes) => ({
                //     ...prevAttributes,
                //     colour: variant?.attributes?.colour,
                //   }));
                // }
                return (
                  <div
                    key={index}
                    tabIndex={0}
                    onClick={() => {
                      handleColorVariant?.(variant?.sku);
                      setSelectedAttributes((prevAttributes) => ({
                        ...prevAttributes,
                        colour: variant?.attributes?.colour,
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleColorVariant?.(variant?.sku);
                        setSelectedAttributes((prevAttributes) => ({
                          ...prevAttributes,
                          colour: variant?.attributes?.colour,
                        }));
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    className="h-64 w-64 rounded-[2px] border border-[#E2E7E8]"
                    title={variant?.attributes?.colour}
                    onMouseEnter={() => {
                      if (window.innerWidth > 768) {
                        (setHoveredImage as any)(img as string);
                      }
                    }}
                    onMouseLeave={() => {
                      if (window.innerWidth > 768) {
                        (setHoveredImage as any)('');
                      }
                    }}
                  >
                    <div className="relative h-full w-full cursor-pointer rounded-md">
                      <div
                        className="h-[95%] w-[95%] bg-cover bg-no-repeat"
                        style={{
                          backgroundImage: `url(${img})`,
                          backgroundPosition: 'center',
                          backgroundSize: `${variant?.attributes?.cropX}px ${variant?.attributes?.cropY}px`,
                        }}
                      ></div>
                      {isActive && <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-[#1DCE8F]" />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <></>
      );
    case 'size':
      const sizeOrder = sizeData?.sizes;

      const sizeContent =
        sizeAccordingToColour?.length > 0 ? discardRepeatedValues(sizeAccordingToColour, 'size') : variants;

      const isPureNumber = (value: string): boolean => /^\d+$/.test(value);

      const extractLeadingNumber = (value: string): number | null => {
        const match = value.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      };

      const getSizeIndex = (size: string): number => {
        const lowerSize = size?.toLowerCase()?.trim();
        const mapped = sizeMap?.find((entry) => entry?.key === lowerSize);
        if (mapped) return mapped?.index;

        if (isPureNumber(lowerSize)) return 1000 + parseInt(lowerSize, 10);
        const extractedNum = extractLeadingNumber(lowerSize);
        if (extractedNum !== null) return 2000 + extractedNum;

        return 9999 + lowerSize?.charCodeAt(0);
      };

      const sortedSizeContent = sizeContent?.sort((a, b) => {
        const sizeA = a?.attributes?.size?.toString() || '';
        const sizeB = b?.attributes?.size?.toString() || '';

        return getSizeIndex(sizeA) - getSizeIndex(sizeB);
      });

      return sortedSizeContent?.length > 0 ? (
        <div className="size-section mt-8 flex flex-col gap-4">
          <div className="flex" style={{ minWidth: '125px' }}>
            <span className="mb-13 mr-[9rem] text-14 font-normal capitalize">
              {formatProductMessage({ id: 'choose.size', defaultMessage: 'Choose Size' })}
            </span>
            {tableSet?.length > 0 && (
              <span
                className="link-arrow dialoglink cursor-pointer text-12 font-normal capitalize underline sm:text-14"
                onClick={showSizeGuide}
              >
                <i>{formatProductMessage({ id: 'sizeGuide', defaultMessage: 'Size guide' })}</i>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-8">
            {sortedSizeContent?.map((i: Variant, index: number) => {
              const isActive =
                // sortedSizeContent?.length === 1 ||
                i?.attributes?.size !== '' && selectedAttributes?.size === i?.attributes?.size;
              // if (selectedAttributes?.size !== i?.attributes?.size) {
              //   setSelectedAttributes((prevAttributes) => ({
              //     ...prevAttributes,
              //     size: i?.attributes?.size,
              //   }));
              // }
              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedAttributes((prevAttributes) => ({
                      ...prevAttributes,
                      size: i?.attributes?.size,
                    }));
                  }}
                  style={{
                    height: '40px',
                    backgroundColor: isActive ? '#2dac70' : '',
                    color: isActive ? 'white' : '',
                    borderColor: isActive ? '#2dac70' : '',
                    minWidth: '40px',
                  }}
                  className="relative mr-4 flex cursor-pointer items-center justify-center rounded-md border border-gray-600 bg-white px-3 pb-5 pt-8 text-14 font-normal leading-4 lg:py-5"
                >
                  {/* {i?.attributes?.size} */}
                  {formatSize(i?.attributes?.size)}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <></>
      );
    case 'shoesize':
      const shoeSizeContent =
        sizeAccordingToColour?.length > 0
          ? discardRepeatedValues(sizeAccordingToColour, 'shoesize').sort(sortVariantsByShoeSize)
          : discardRepeatedValues(variants, 'shoesize').sort(sortVariantsByShoeSize);
      return variants?.length > 0 ? (
        <div className="size-section mt-8 flex flex-col gap-4">
          <div className="flex" style={{ minWidth: '125px' }}>
            <span className="mb-13 mr-[9rem] text-14 font-normal capitalize">
              {formatProductMessage({ id: 'choose.choose.shoesize', defaultMessage: 'Choose Shoe Size' })}
            </span>
            {tableSet?.length > 0 && (
              <span
                className="link-arrow dialoglink cursor-pointer text-12 font-normal capitalize underline sm:text-14"
                onClick={showSizeGuide}
              >
                <i>{formatProductMessage({ id: 'sizeGuide', defaultMessage: 'Size guide' })}</i>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-8">
            {shoeSizeContent?.map((i: Variant, index: number) => {
              const isActive =
                // shoeSizeContent?.length === 1 ||
                i?.attributes?.shoesize !== '' && selectedAttributes?.shoesize === i?.attributes?.shoesize;
              // if (shoeSizeContent?.length === 1 && selectedAttributes?.shoesize !== i?.attributes?.shoesize) {
              //   setSelectedAttributes((prevAttributes) => ({
              //     ...prevAttributes,
              //     shoesize: i?.attributes?.shoesize,
              //   }));
              // }
              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedAttributes((prevAttributes) => ({
                      ...prevAttributes,
                      shoesize: i?.attributes?.shoesize,
                    }));
                  }}
                  style={{
                    height: '40px',
                    backgroundColor: isActive ? '#2dac70' : '',
                    color: isActive ? 'white' : '',
                    borderColor: isActive ? '#2dac70' : '',
                    minWidth: '40px',
                  }}
                  className=" relative flex cursor-pointer items-center justify-center rounded-md border border-gray-600 bg-white px-3 pb-5 pt-8 text-center text-14 font-normal leading-4 lg:py-5"
                >
                  {i?.attributes?.shoesize}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <></>
      );
    case 'waistsize':
      const waistSizeContent =
        WaistAccordingTocolor?.length > 0 ? discardRepeatedValues(WaistAccordingTocolor, 'waist') : variants;
      const sortedVariants = waistSizeContent?.slice().sort((a, b) => {
        const waistA = parseInt(a?.attributes?.waist || '0', 10);
        const waistB = parseInt(b?.attributes?.waist || '0', 10);
        return waistA - waistB;
      });
      return sortedVariants?.length > 0 ? (
        <div className="size-section mt-8 flex flex-col gap-4">
          <div className="flex" style={{ minWidth: '125px' }}>
            <span className="mb-13 mr-[9rem] text-14 font-normal capitalize">
              {formatProductMessage({ id: 'choose.waistsize', defaultMessage: 'Choose Waist Size' })}
            </span>
            {tableSet?.length > 0 && (
              <span
                className="link-arrow dialoglink cursor-pointer text-12 font-normal capitalize underline sm:text-14"
                onClick={showSizeGuide}
              >
                <i>{formatProductMessage({ id: 'sizeGuide', defaultMessage: 'Size guide' })}</i>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-8">
            {sortedVariants?.map((i: Variant, index: number) => {
              const isActive =
                // sortedVariants.length === 1 ||
                i?.attributes?.waist !== '' && selectedAttributes?.waist === i?.attributes?.waist;
              // if (sortedVariants?.length === 1 && selectedAttributes?.waist !== i?.attributes?.waist) {
              //   setSelectedAttributes((prevAttributes) => ({
              //     ...prevAttributes,
              //     waist: i?.attributes?.waist,
              //   }));
              // }
              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedAttributes((prevAttributes) => ({
                      ...prevAttributes,
                      waist: i?.attributes?.waist,
                    }));
                  }}
                  style={{
                    height: '40px',
                    backgroundColor: isActive ? '#2dac70' : '',
                    color: isActive ? 'white' : '',
                    borderColor: isActive ? '#2dac70' : '',
                    minWidth: '40px',
                  }}
                  className="relative mr-4 flex cursor-pointer items-center justify-center rounded-md border border-gray-600 bg-white px-3 pb-5 pt-8 text-14 font-normal leading-4 lg:py-5"
                >
                  {i?.attributes?.waist}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <></>
      );
    case 'leglength': {
      const legLengthOrder = sizeData.legLength;
      const legLengthContent =
        legSizeAccordingToSizeAndColour?.length > 0
          ? discardRepeatedValues(legSizeAccordingToSizeAndColour, 'leg')
          : legSizeAccordingToWiastAndColour?.length > 0
          ? discardRepeatedValues(legSizeAccordingToWiastAndColour, 'leg')
          : legSizeAccordingToWiast?.length > 0
          ? discardRepeatedValues(legSizeAccordingToWiast, 'leg')
          : variants;
      console.log('==legLengthCoentnt', legLengthContent);
      const sortedLegLengthContent = legLengthContent?.sort((a, b) => {
        const legA = a?.attributes?.leg.toLowerCase();
        const legB = b?.attributes?.leg.toLowerCase();
        return legLengthOrder.indexOf(legA) - legLengthOrder.indexOf(legB);
      });
      const isDisabled = !(selectedAttributes?.waist || selectedAttributes?.size || selectedAttributes?.shoesize);
      return sortedLegLengthContent?.length > 0 ? (
        <div className="size-section mt-8 flex flex-col gap-4">
          <div className="flex" style={{ minWidth: '125px' }}>
            <span className="mb-13 text-14 font-normal capitalize">
              {formatProductMessage({ id: 'choose.leglength', defaultMessage: 'Choose Leg Length' })}
            </span>
          </div>
          <div className="flex flex-wrap gap-8">
            {sortedLegLengthContent?.map((i: Variant, index: number) => {
              const isActive =
                // sortedLegLengthContent?.length === 1 ||
                i?.attributes?.leg !== '' && selectedAttributes?.leg === i?.attributes?.leg;
              // if (sortedLegLengthContent?.length === 1 && selectedAttributes?.leg !== i?.attributes?.leg) {
              //   setSelectedAttributes((prevAttributes) => ({
              //     ...prevAttributes,
              //     leg: i?.attributes?.leg,
              //   }));
              // }
              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedAttributes((prevAttributes) => ({
                      ...prevAttributes,
                      leg: i?.attributes?.leg,
                    }));
                  }}
                  style={{
                    height: '40px',
                    backgroundColor: isActive ? '#2dac70' : '',
                    color: isActive ? 'white' : '',
                    borderColor: isActive ? '#2dac70' : '',
                    minWidth: '40px',
                  }}
                  className={`relative mr-4 flex items-center justify-center rounded-md border border-gray-600 bg-white px-3 pb-5 pt-8 text-14 font-normal capitalize leading-4 lg:py-5
                     ${
                       !isActive && isDisabled
                         ? 'pointer-events-none cursor-not-allowed line-through opacity-50'
                         : 'cursor-pointer'
                     }`}
                >
                  {i?.attributes?.leg}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <></>
      );
    }
    case 'hand':
      const handContent =
        handAccordingToSizeAndColour?.length > 0
          ? discardRepeatedValues(handAccordingToSizeAndColour, 'hand')
          : handAccordingToSize?.length > 0
          ? discardRepeatedValues(handAccordingToSize, 'hand')
          : variants;

      return handContent?.length > 0 ? (
        <div className="hand-section mt-8 flex flex-col gap-4">
          <div className="flex">
            <span className="mb-13 text-14 font-normal capitalize">
              {formatProductMessage({ id: 'choose.hand', defaultMessage: 'Select The Hand You Wear Your Glove On' })}
            </span>
          </div>
          <div className="flex flex-wrap gap-8">
            {handContent?.map((i: Variant, index: number) => {
              const isActive =
                // handContent?.length === 1 ||
                i?.attributes?.hand !== '' && selectedAttributes?.hand === i?.attributes?.hand;
              // if (handContent?.length === 1 && selectedAttributes?.hand !== i?.attributes?.hand) {
              //   setSelectedAttributes((prevAttributes) => ({
              //     ...prevAttributes,
              //     hand: i?.attributes?.hand,
              //   }));
              // }
              return (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedAttributes((prevAttributes) => ({
                      ...prevAttributes,
                      hand: i?.attributes?.hand,
                    }));
                  }}
                  style={{
                    height: '40px',
                    backgroundColor: isActive ? '#2dac70' : '',
                    color: isActive ? 'white' : '',
                    borderColor: isActive ? '#2dac70' : '',
                    minWidth: '40px',
                  }}
                  className=" relative flex cursor-pointer items-center justify-center rounded-md border border-gray-600 bg-white px-3 pb-5 pt-8 text-14 font-normal capitalize leading-4 lg:py-5"
                >
                  {i?.attributes?.hand}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <></>
      );
    default:
      return null;
  }
};

export default VariantOptions;
